import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icons.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useBrand } from '../../contexts/BrandContext.jsx';
import { useDashboardData } from '../../state/DashboardDataContext.jsx';
import { useContentFeedContext } from '../../state/ContentFeedContext.jsx';
import { isPostingPlan } from '../../lib/finishSetup.js';
import { firstPostReadyKey, markFirstPostOpened, recentReadyFirstPost } from '../../lib/firstPostReady.js';

// Completion fallback for setup performed in ChatGPT or on another device.
// The feed already refreshes visible server-initiated work once per minute.
export default function FirstPostReadyCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const brand = useBrand();
  const dash = useDashboardData() || {};
  const feed = useContentFeedContext() || {};
  const [now, setNow] = useState(Date.now);
  const [dismissedKey, setDismissedKey] = useState(null);
  const brandId = dash.setupStatus?.profile?.id;
  const brandMatches = !brand.enabled || (!brand.loading && brand.activeBrandId === brandId);
  const eligible = dash.accountId === user?.id && brandMatches
    && Boolean(dash.fetchedAt?.entitlements) && !isPostingPlan(dash.entitlements)
    && feed.hasLoaded && !feed.loading && !feed.error && dash.setupStatus?.brand === 'present';
  const post = eligible ? recentReadyFirstPost({
    content: feed.content,
    userId: user?.id,
    organizationId: user?.organizationId,
    brandId,
    now: Math.max(now, Date.now()),
  }) : null;
  const key = firstPostReadyKey(user?.id, post?.id);
  let alreadyOpened = false;
  try { alreadyOpened = Boolean(key && window.localStorage.getItem(key) === '1'); } catch (_) { /* best effort */ }

  useEffect(() => {
    if (!post) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, post.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [post?.expiresAt]);

  if (!post || alreadyOpened || dismissedKey === key) return null;
  return (
    <section className="card sj-done" role="status">
      <span className="sj-ok"><Icon name="check" size={14} /></span>
      <div className="sj-tx">
        <div className="sj-tt">{t('journey.doneTitle')}</div>
        <div className="sj-ts">{t('journey.doneSub')}</div>
      </div>
      <button type="button" className="btn btn-primary sj-cta" onClick={() => navigate(`/app/reveal/${encodeURIComponent(post.id)}`)}>
        {t('journey.doneCta')} <Icon name="arrowRight" size={13} />
      </button>
      <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('common.close')} onClick={() => {
        markFirstPostOpened(user.id, post.id);
        setDismissedKey(key);
      }}>×</button>
    </section>
  );
}
